import { TypeSystem } from "../../support/type-system.support";
import { OrderEventOriginType } from "../dto/order-event-origin-type.enum";
import { OrderEventItemForCreation } from "./order-event-item-for-creation.model";
import { OrderEventProductsForCreation } from "./order-event-products-for-creation.model";
import { ReplacementItemForCreation } from "./replacement-item-for-creation.model";

	/**
	 * @description
	 *  Modelo de una nota de CyR (cambio y reclamo), o NC (nota de crédito),
	 *	sobre un pedido para creación.
	 *
	 * @see gigot-backend/ViewModels/OrderEvent/OrderEventForCreationDto.cs
	 */

export class OrderEventForCreation {

	public applicationUserId: string;
	public descriptionEvent: string;
	public generateCreditNote: boolean;
	public orderEventItems: OrderEventItemForCreation[];
	public orderEventOriginType: OrderEventOriginType;
	public orderEventProducts: OrderEventProductsForCreation[];
	public orderEventTypeId: number;
	public orderId: number;
	public replacementItems: ReplacementItemForCreation[];

	/**
	 * @description
	 *	Construye una nota con una semántica vacía. Nótese que no es lo mismo
	 *	que construir una nota con valores por defecto. Las reglas generales
	 *	de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Una nota de CyR/NC vacía.
	 */
	public static empty(): OrderEventForCreation {
		const orderEventForCreation: OrderEventForCreation = new OrderEventForCreation();
		orderEventForCreation.applicationUserId = "";
		orderEventForCreation.descriptionEvent = "";
		orderEventForCreation.generateCreditNote = false;
		orderEventForCreation.orderEventItems = [];
		orderEventForCreation.orderEventOriginType = undefined;
		orderEventForCreation.orderEventProducts = [];
		orderEventForCreation.orderEventTypeId = undefined;
		orderEventForCreation.orderId = undefined;
		orderEventForCreation.replacementItems = [];
		return orderEventForCreation;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicOrderEventForCreation
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicOrderEventForCreation: OrderEventForCreation): OrderEventForCreation {
		if (!anemicOrderEventForCreation) return undefined;
		const orderEventForCreation: OrderEventForCreation = OrderEventForCreation.empty();
		orderEventForCreation.applicationUserId = anemicOrderEventForCreation.applicationUserId;
		orderEventForCreation.descriptionEvent = anemicOrderEventForCreation.descriptionEvent;
		orderEventForCreation.generateCreditNote = anemicOrderEventForCreation.generateCreditNote;
		orderEventForCreation.orderEventItems = TypeSystem.orElse(anemicOrderEventForCreation.orderEventItems, [])
			.map(orderEventItemForCreation => OrderEventItemForCreation.from(orderEventItemForCreation));
		orderEventForCreation.orderEventOriginType = anemicOrderEventForCreation.orderEventOriginType;
		orderEventForCreation.orderEventProducts = TypeSystem.orElse(anemicOrderEventForCreation.orderEventProducts, [])
			.map(orderEventProductForCreation => OrderEventProductsForCreation.from(orderEventProductForCreation));
		orderEventForCreation.orderEventTypeId = anemicOrderEventForCreation.orderEventTypeId;
		orderEventForCreation.orderId = anemicOrderEventForCreation.orderId;
		orderEventForCreation.replacementItems = TypeSystem.orElse(anemicOrderEventForCreation.replacementItems, [])
			.map(replacementItemForCreation => ReplacementItemForCreation.from(replacementItemForCreation));
		return orderEventForCreation;
	}
}
